"use client";

import { useState } from "react";
import { AI_CONSENT_VERSION } from "@lib/consent/aiConsent";
import { TERMS_VERSION, TERMS_URL, PRIVACY_URL } from "@lib/legal";
import { useTranslations } from "@i18n/provider";

export function AiConsentPanel({ onConsented }: { onConsented: () => void }) {
  const { t, locale } = useTranslations();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [memory, setMemory] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (busy) return;
    if (!accepted || !termsAccepted) {
      setError(t("aiConsent.needsRequired"));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/consent/ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accepted: true,
          tutorMemory: memory,
          version: AI_CONSENT_VERSION,
          locale,
          terms: { accepted: true, version: TERMS_VERSION },
        }),
      });
      if (!res.ok) {
        setError(t(res.status === 409 ? "aiConsent.outdated" : "aiConsent.error"));
        return;
      }
      onConsented();
    } catch {
      setError(t("aiConsent.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="ai-consent-panel" className="flex flex-col gap-3 p-4 text-sm">
      <h2 className="font-display text-base font-bold text-ink">{t("aiConsent.title")}</h2>
      <p className="text-ink-body">{t("aiConsent.recipients")}</p>
      <p className="text-ink-muted">{t("aiConsent.noTraining")}</p>

      <label className="flex items-start gap-2 text-ink-body">
        <input
          type="checkbox"
          disabled={busy}
          data-testid="consent-terms"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          className="mt-1"
        />
        <span>{t("aiConsent.terms")}</span>
      </label>
      <p className="flex flex-wrap gap-4">
        <a className="underline" href={TERMS_URL} target="_blank" rel="noopener noreferrer">
          {t("billing.legal.terms")}
        </a>
        <a className="underline" href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
          {t("billing.legal.privacy")}
        </a>
      </p>
      <p className="text-ink-muted">{t("aiConsent.privacyNotice")}</p>

      <label className="flex items-start gap-2 text-ink-body">
        <input
          type="checkbox"
          disabled={busy}
          data-testid="ai-consent-required"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          className="mt-1"
        />
        <span>{t("aiConsent.required")}</span>
      </label>

      <label className="flex items-start gap-2 text-ink-body">
        <input
          type="checkbox"
          disabled={busy}
          data-testid="ai-consent-memory"
          checked={memory}
          onChange={(e) => setMemory(e.target.checked)}
          className="mt-1"
        />
        <span>{t("aiConsent.memory")}</span>
      </label>

      <div className="rounded-lg bg-surface p-3 text-sm">
        <p className="font-semibold text-ink">{t("aiConsent.memoryHeading")}</p>
        <p className="mt-1 text-ink-body">{t("aiConsent.memoryOn")}</p>
        <p className="mt-1 text-ink-body">{t("aiConsent.memoryOff")}</p>
        <p className="mt-2 text-ink-muted">{t("aiConsent.memoryPrivacy")}</p>
        <p className="mt-1 text-ink-muted">{t("aiConsent.memoryControl")}</p>
      </div>

      {error ? (
        <p role="alert" className="text-writing-fg">
          {error}
        </p>
      ) : null}

      <button
        type="button"
        data-testid="ai-consent-submit"
        onClick={() => void submit()}
        // The guard is repeated in `submit`, so a disabled attribute alone is
        // not the only thing between a half-consented learner and a request.
        disabled={!accepted || !termsAccepted || busy}
        className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-50"
      >
        {t("aiConsent.accept")}
      </button>
    </div>
  );
}
